import React, { useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import TableCell from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import {
  Box,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Popover,
} from "@mui/material";
import CustomTypography from "../../globalStyles/fonts";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import { isMobile } from "../../globalStyles/mobile";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import EditIcon from "../../globalStyles/icons/editIcon";
import ViewIcon from "../../globalStyles/icons/vieewIcon";
import { useTheme } from "@mui/styles";
import { supplierClientMapService } from "../../utils/services/supplier-client-map.service";
import { toast } from "react-toastify";
import SupplierClientMapDetailsModal from "../supplier-client-map-details-modal";
import TableHeader from "../table-header";
import { useDarkMode } from "../context/darkModeContext";

const StyledTableCell = styled(TableCell)(() => ({
  "&.MuiTableCell-head": {
    backgroundColor: "var(--table-header-bg)",
    padding: "8px",
    textAlign: "start",
  },
  "&.MuiTableCell-body": {
    fontSize: 12,
    padding: "8px",
    textAlign: "start",
    color: "var(--text-primary)",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--row-bg-odd)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
  },
  "&:lastChild td, &:lastChild th": {
    border: 0,
  },
}));

const ShippingServiceMapTableCell = ({
  shippingService,
  handleViewClick,
  handleEditClick,
  handleDeleteClick,
  handleClientAssignClick,
  colors,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handlePopoverOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  const handleActionClick = (action) => {
    handlePopoverClose();
    switch (action) {
      case "view":
        handleViewClick(shippingService);
        break;
      case "edit":
        handleEditClick(shippingService);
        break;
      case "delete":
        handleDeleteClick(shippingService);
        break;
      case "assign":
        handleClientAssignClick(shippingService);
        break;
      default:
        break;
    }
  };

  return (
    <Box>
      <IconButton
        aria-label="more actions"
        onClick={handlePopoverOpen}
        sx={{
          color: open ? "primary.main" : "inherit",
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Popover
        PaperProps={{
          sx: {
            backgroundColor: colors.bgPaper,
            border: "1px solid " + colors.border,
          },
        }}
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", padding: "10px" }}>
          <Button
            onClick={() => handleActionClick("edit")}
            sx={{ textTransform: "none", justifyContent: "flex-start" }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              <EditIcon />
              <Typography
                fontFamily={"poppins"}
                fontSize={"16px"}
                color={colors.textPrimary}
                fontWeight={500}
              >
                Edit
              </Typography>
            </Box>
          </Button>
          <Button
            onClick={() => handleActionClick("view")}
            sx={{ textTransform: "none", justifyContent: "flex-start" }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              <ViewIcon />
              <Typography
                fontFamily={"poppins"}
                fontSize={"16px"}
                color={colors.textPrimary}
                fontWeight={500}
              >
                View
              </Typography>
            </Box>
          </Button>

          <Button
            onClick={() => handleActionClick("delete")}
            sx={{ textTransform: "none", justifyContent: "flex-start" }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              <DeleteIcon />
              <Typography
                fontFamily={"poppins"}
                fontSize={"16px"}
                color={colors.textPrimary}
                fontWeight={500}
              >
                Delete
              </Typography>
            </Box>
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};
const SupplierClientMapList = ({
  data,
  onEditData,
  onDeleteData,
  viewMode,
  selectedService,
  setSelectedShippingService,
  currentPage,
  itemsPerPage,
}) => {
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [supplierClientMapList, setSupplierClientMapList] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const startIndex = currentPage * itemsPerPage;
  const theme = useTheme();
  const defaultColor = "#D13438";
  const primary_color = theme.palette?.primary?.main || defaultColor;
  const clientId = localStorage.getItem("client_id");
  const { colors } = useDarkMode();

  const handleDeleteClick = (shippingService) => {
    setSelectedShippingService(shippingService);
    setOpenConfirmation(true);
  };

  const confirmDelete = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await supplierClientMapService.delete(
        selectedService._id,
      );
      onDeleteData && onDeleteData();
      toast.success("Supplier client mapping deleted sucessfully.");
      setOpenConfirmation(false);
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = (shippingService) => {
    setSelectedShippingService(shippingService);
    setOpenViewModal(true);
  };

  const handleClientAssignClick = (x) => {
    setSelectedShippingService(x);
    setModalOpen(true);
  };
  useEffect(() => {
    setSupplierClientMapList(
      data?.map((shippingService) => ({
        ...shippingService,
        action: (
          <ShippingServiceMapTableCell
            shippingService={shippingService}
            handleViewClick={handleViewClick}
            handleEditClick={onEditData}
            handleDeleteClick={handleDeleteClick}
            handleClientAssignClick={handleClientAssignClick}
            colors={colors}
          />
        ),
      })),
    );
  }, [data, colors]);

  const getNestedValue = (obj, path) => {
    if (!obj || !path) return undefined;
    return path.split(".").reduce((acc, key) => acc?.[key], obj);
  };
  const NoDataImage = () => (
    <div
      style={{
        width: "100%",
        justifyContent: "center",
        display: "flex",
      }}
    >
      <img
        src="no_data_product.png"
        alt="No data available"
        style={{ objectFit: "cover", height: "300px" }}
      />
    </div>
  );

  const TableSchema = [
    {
      key: "sl_no",
      title: "SL NO",
      render: (item, index) => (
        <Typography
          fontSize="12px"
          color={colors.textSecondary}
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {startIndex + index + 1}
        </Typography>
      ),
    },
    {
      key: "client.business_name",
      title: "CLIENT",
      displayKey: "client.business_name",
    },
    {
      key: "supplier.name",
      title: "SUPPLIER",
      displayKey: "supplier.name",
    },

    ...(clientId
      ? []
      : [{ key: "action", title: "", width: "3%", displayKey: "action" }]),
  ];
  const renderGridView = () => (
    <Grid
      sx={{
        marginTop: "1px",
        maxHeight: "100%",
        paddingBottom: 5,
        overflow: "auto",
      }}
      container
      spacing={2}
    >
      {supplierClientMapList?.map((supplierClientMap, index) => (
        <Grid sx={{ paddingBottom: 1 }} item xs={12} sm={6} md={4} key={index}>
          <Card
            sx={{
              boxShadow: 3,
              cursor: "pointer",
              width: "100%",
              height: "100%",
              position: "relative",
              borderRadius: "6px",
              border: "1px solid " + colors.border,
              backgroundColor: colors.bgPaper,
            }}
          >
            <CardContent>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  fontWeight="500"
                  sx={{ textAlign: "start" }}
                  fontFamily="Poppins"
                  color={colors.textPrimary}
                >
                  {startIndex + index + 1}
                </Typography>
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                  }}
                >
                  {supplierClientMap?.action}
                </div>
              </div>
              <Grid container spacing={1} mt={2}>
                <Grid item xs={5} display="flex" alignItems="center">
                  <Typography
                    fontWeight="500"
                    sx={{ textAlign: "start" }}
                    fontFamily="Poppins"
                    color={colors.textPrimary}
                  >
                    SUPPLIER
                  </Typography>
                </Grid>

                <Grid item xs={7}>
                  <Tooltip title={supplierClientMap?.supplier?.name} arrow>
                    <Typography
                      noWrap
                      sx={{ textAlign: "start" }}
                      fontWeight="500"
                      fontFamily={"poppins"}
                      color={colors.textPrimary}
                    >
                      : {supplierClientMap?.supplier?.name}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={5} display={"flex"} alignItems={"center"}>
                  <Typography
                    fontWeight="500"
                    sx={{ textAlign: "start" }}
                    fontFamily={"poppins"}
                    color={colors.textPrimary}
                  >
                    Client
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Tooltip
                    title={supplierClientMap?.client?.business_name}
                    arrow
                  >
                    <Typography
                      noWrap
                      sx={{ textAlign: "start" }}
                      fontWeight="500"
                      fontFamily={"poppins"}
                      color={colors.textPrimary}
                    >
                      : {supplierClientMap?.client?.business_name}
                    </Typography>
                  </Tooltip>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderListView = () => (
    <div
      style={{
        width: "100%",
        overflow: isMobile() ? "auto" : "hidden",
        paddingBottom: 20,
        marginTop: 5,
      }}
    >
      <TableContainer
        sx={{
          overflowY: "auto",
          paddingInline: 0.4,
          maxHeight: "100%",
        }}
      >
        <Table
          stickyHeader
          aria-label="sticky table"
          sx={{
            border: "1px solid " + colors.border,
          }}
        >
          <TableHead
            sx={{
              backgroundColor: colors.bgHeader + " !important",
              "& th": { backgroundColor: colors.bgHeader },
            }}
          >
            <StyledTableRow>
              {TableSchema.map((column) => (
                <TableHeader
                  key={column.key}
                  column={column}
                  filterKey={column.filterKey}
                  handleOpenFilter={column.handleOpenFilter}
                  title={column.title}
                />
              ))}
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {supplierClientMapList.length === 0 ? (
              <StyledTableRow>
                <TableCell colSpan={8} align="center">
                  <NoDataImage />
                </TableCell>
              </StyledTableRow>
            ) : (
              supplierClientMapList?.map((x, rowIndex) => (
                <StyledTableRow key={rowIndex}>
                  {TableSchema.map((column, colIndex) => (
                    <StyledTableCell
                      key={column.key || colIndex}
                      sx={{ width: column.width }}
                    >
                      {column.displayKey ? (
                        <Typography
                          fontSize="12px"
                          color={colors.textSecondary}
                          fontWeight={400}
                          fontFamily={"Poppins"}
                        >
                          {getNestedValue(x, column.displayKey) || "--"}
                        </Typography>
                      ) : column.render ? (
                        column.render(x, rowIndex)
                      ) : (
                        "--"
                      )}
                    </StyledTableCell>
                  ))}
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );

  return (
    <div sx={{ minWidth: "100%" }}>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
            color: "white",
          }}
        >
          <CircularProgress color="inherit" />
        </Box>
      ) : (
        <div
          style={{
            overflowY: "scroll",
            display: "flex",
            maxHeight: isMobile() ? "60vh" : `calc(100vh - 200px)`,
            width: "100%",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::WebkitScrollbar": { width: 0 },
          }}
        >
          {viewMode === "grid" || isMobile()
            ? renderGridView()
            : renderListView()}
        </div>
      )}

      <DeleteConfirmationModal
        DeleteConfirmationModal
        title={`Are you sure you want to delete ${selectedService?.supplier?.name} ?`}
        open={openConfirmation}
        handleClose={() => setOpenConfirmation(false)}
        handleDelete={(e) => {
          confirmDelete(e);
        }}
      />

      <SupplierClientMapDetailsModal
        clientMap={selectedService}
        open={openViewModal}
        onClose={() => setOpenViewModal(false)}
      />
    </div>
  );
};

export default SupplierClientMapList;
